import { AlertTriangle, BookOpen, Lightbulb, Star, type LucideIcon } from 'lucide-react';

import type {
  CalloutContent,
  ScriptElement,
} from '../../schema';

/**
 * Callout element (INTEGRATION §3.2 / BLUEPRINT §13). Variant chrome:
 * common_mistake = red, memory_trick = amber, important_concept = blue.
 *
 * PARITY: this card must be laid out identically to the authoring canvas's
 * `src/components/canvas/elements/CalloutElement.tsx`, because an author places
 * the callout against THAT rendering. It previously drew a different card — no
 * leading icon, 18px padding, a 2px border and a 14px radius against the
 * canvas's icon + 8px gap + 12px padding + 1px border + 8px radius — which put
 * the visible text about 19px left of where it sits on the canvas, so callouts
 * looked slightly displaced in preview even with no rotation. Palette, icons
 * and metrics below mirror the admin's `src/lib/callout.ts`; keep them in step.
 */
const CHROME: Record<
  string,
  { bg: string; border: string; accent: string; label: string; Glyph: LucideIcon }
> = {
  common_mistake: {
    bg: '#fef2f2',
    border: '#fecaca',
    accent: '#dc2626',
    label: 'Common mistake',
    Glyph: AlertTriangle,
  },
  memory_trick: {
    bg: '#fffbeb',
    border: '#fde68a',
    accent: '#d97706',
    label: 'Memory trick',
    Glyph: Lightbulb,
  },
  important_concept: {
    bg: '#eff6ff',
    border: '#bfdbfe',
    accent: '#2563eb',
    label: 'Important concept',
    Glyph: Star,
  },
  concept: {
    bg: '#f8fafc',
    border: '#e2e8f0',
    accent: '#f59e0b',
    label: 'Concept',
    Glyph: BookOpen,
  },
};

export function CalloutElement({
  element,
  fill,
}: {
  element: ScriptElement;
  fill?: boolean;
}) {
  const content = element.content as CalloutContent | undefined;
  if (!content) return null;
  // Authored text size drives the body; titles scale with it.
  const bodySize = (element.style?.fontSize as number | undefined) ?? 20;
  const textAlign =
    (element.style?.textAlign as 'left' | 'center' | 'right' | 'justify' | undefined) ?? 'left';
  const cardShadow = content.shadow ? '0 10px 24px rgba(15, 23, 42, 0.18)' : undefined;

  // `concept` — minimal definition card: thick left bar (authored color, amber
  // default), bold title in normal case, regular body. No icon, no chrome.
  if (content.variant === 'concept') {
    return (
      <div
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderLeft: `5px solid ${content.accentColor ?? '#f59e0b'}`,
          borderRadius: 10,
          padding: '12px 16px',
          width: '100%',
          boxShadow: cardShadow,
          textAlign,
          ...(fill ? { height: '100%' } : null),
        }}
      >
        {content.title?.trim() && (
          <div
            style={{
              fontSize: Math.round(bodySize * 0.9),
              fontWeight: 700,
              color: '#1e293b',
              marginBottom: 4,
            }}
          >
            {content.title}
          </div>
        )}
        <div
          style={{
            fontSize: bodySize,
            fontWeight: 400,
            color: '#334155',
            whiteSpace: 'pre-line',
            lineHeight: 1.45,
          }}
        >
          {content.body}
        </div>
      </div>
    );
  }

  const chrome = CHROME[content.variant] ?? CHROME.important_concept;
  const { Glyph } = chrome;

  // Mirrors the canvas card exactly: flex row, 8px gap, 12px padding, 1px
  // border, 8px radius, leading 18px glyph nudged 2px down (`mt-0.5`).
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        background: chrome.bg,
        border: `1px solid ${chrome.border}`,
        borderRadius: 8,
        padding: 12,
        width: '100%',
        boxShadow: cardShadow,
        ...(fill ? { height: '100%' } : null),
      }}
    >
      <Glyph size={18} color={chrome.accent} style={{ marginTop: 2, flexShrink: 0 }} />
      <div style={{ minWidth: 0, textAlign }}>
        <div
          style={{
            fontSize: Math.max(11, Math.round(bodySize * 0.7)),
            fontWeight: 600,
            color: chrome.accent,
          }}
        >
          {content.title?.trim() || chrome.label}
        </div>
        <div
          style={{
            fontSize: bodySize,
            color: (element.style?.color as string | undefined) ?? '#1f2937',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.4,
          }}
        >
          {content.body}
        </div>
      </div>
    </div>
  );
}
